/**
 * Compact UI-language switcher rendered in the user menu.
 * Only switches interface chrome; meeting summary language is a separate
 * toggle on the meeting detail page.
 */

import { isUiLocale, type TUiLocale } from "@lexicanon/shared"
import { msg } from "@lingui/core/macro"
import { useLingui } from "@lingui/react"
import { Trans } from "@lingui/react/macro"
import { Menu } from "@mantine/core"
import { updateUiLocale } from "../../lib/api/ui-locale"
import { refreshSession } from "../../lib/auth-client"
import { activateLocale, LOCALE_LABELS, SUPPORTED_LOCALES, type TLocale } from "../../lib/i18n"
import { notifyError } from "../../lib/notify"

export function LocaleSwitcherMenuItems(): React.JSX.Element {
  const { i18n } = useLingui()
  const current: TLocale = isUiLocale(i18n.locale) ? i18n.locale : "en"

  const handlePick = (code: TUiLocale): void => {
    void activateLocale(code, { persist: true })
    void updateUiLocale(code)
      .then(() => refreshSession())
      .catch((err: unknown) => {
        notifyError(err, i18n._(msg`Could not save language`))
      })
  }

  return (
    <>
      <Menu.Label>
        <Trans>Interface language</Trans>
      </Menu.Label>
      {SUPPORTED_LOCALES.map((code) => (
        <Menu.Item
          key={code}
          onClick={() => {
            handlePick(code)
          }}
          rightSection={current === code ? "✓" : null}
          data-testid={`ui-locale-${code}`}
        >
          {LOCALE_LABELS[code]}
        </Menu.Item>
      ))}
    </>
  )
}
